import { useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useAnimateWhenInView } from '@/core/hooks/use-animate-when-in-view';
import { Card, Text } from '@/ui';

const PLOT_HEIGHT = 300;
const TOOLTIP_WIDTH = 300;

type StackedBarSeries = {
  key: string;
  label: string;
  color: string;
};

type StackedBar = {
  label: string;
  detailLabel?: string;
  values: Record<string, number>;
  labelCaptions?: Record<string, string>;
};

type StackedBarChartProps = {
  bars: StackedBar[];
  series: StackedBarSeries[];
  formatValue: (value: number) => string;
  formatAxis: (value: number) => string;
  totalSuffix?: string;
  /** Keys included in the tooltip/mobile header total (defaults to all series). */
  totalSeriesKeys?: readonly string[];
};

type MonthEarningsDetailProps = {
  bar: StackedBar;
  series: StackedBarSeries[];
  totalSeriesKeys?: readonly string[];
  formatValue: (value: number) => string;
  totalSuffix?: string;
  variant?: 'tooltip' | 'mobile';
  styles: {
    axisLabel: object;
    mobileDetailContent: object;
    mobileDetailHeader: object;
    mobileDetailRows: object;
    mobileDetailRow: object;
    mobileDetailValue: object;
    tooltipRow: object;
    swatch: object;
    tooltipLabel: object;
    tooltipCaption: object;
    tooltipValue: object;
  };
};

function MonthEarningsDetail({
  bar,
  series,
  totalSeriesKeys,
  formatValue,
  totalSuffix,
  variant = 'tooltip',
  styles,
}: MonthEarningsDetailProps) {
  const total = series
    .filter((item) => !totalSeriesKeys || totalSeriesKeys.includes(item.key))
    .reduce((sum, item) => sum + (bar.values[item.key] ?? 0), 0);
  const rows = series.map((item) => {
    const caption = bar.labelCaptions?.[item.key];

    return (
      <View
        key={item.key}
        style={
          variant === 'mobile' ? styles.mobileDetailRow : styles.tooltipRow
        }
      >
        <View
          style={[
            styles.swatch,
            variant === 'mobile' ? styles.swatchRound : undefined,
            { backgroundColor: item.color },
          ]}
        />
        <Text type="caption1" style={styles.tooltipLabel} numberOfLines={2}>
          {item.label}
          {caption ? (
            <Text type="caption1" style={styles.tooltipCaption}>
              {` (${caption})`}
            </Text>
          ) : null}
        </Text>
        <Text
          type="caption1"
          style={
            variant === 'mobile'
              ? styles.mobileDetailValue
              : styles.tooltipValue
          }
        >
          {formatValue(bar.values[item.key] ?? 0)}
        </Text>
      </View>
    );
  });

  if (variant === 'mobile') {
    return (
      <View style={styles.mobileDetailContent}>
        <View style={styles.mobileDetailHeader}>
          <Text type="caption1" style={styles.axisLabel}>
            {bar.detailLabel ?? bar.label}
          </Text>
          <Text type="bodyRegular" style={styles.mobileDetailTotal}>
            {formatValue(total)}
            {totalSuffix ?? ''}
          </Text>
        </View>
        <View style={styles.mobileDetailRows}>{rows}</View>
      </View>
    );
  }

  return (
    <>
      <Text type="caption1" style={styles.axisLabel}>
        {bar.detailLabel ?? bar.label}
      </Text>
      <Text type="bodyBold">
        {formatValue(total)}
        {totalSuffix ?? ''}
      </Text>
      {rows}
    </>
  );
}

const FIVE_THOUSAND_DOLLARS_CENTS = 500_000;

function axisMax(peak: number) {
  if (peak <= 0) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const normalized = peak / magnitude;
  const step =
    normalized <= 1
      ? magnitude
      : normalized <= 2
      ? magnitude * 2
      : normalized <= 5
      ? magnitude * 5
      : magnitude * 10;

  return step;
}

function mobileAxisMax(peak: number) {
  if (peak <= 0) {
    return 1;
  }

  return (
    Math.ceil(peak / FIVE_THOUSAND_DOLLARS_CENTS) * FIVE_THOUSAND_DOLLARS_CENTS
  );
}

function isJanuaryChartLabel(label: string) {
  return label === 'Jan' || label.startsWith('Jan ');
}

function januaryLabelParts(label: string) {
  if (!isJanuaryChartLabel(label)) {
    return null;
  }

  const [, year] = label.split(' ');

  return {
    month: 'Jan',
    year,
  };
}

function shouldShowSparseMonthLabel(
  index: number,
  labels: string[],
  enabled: boolean
) {
  if (!enabled) {
    return true;
  }

  const label = labels[index];

  if (isJanuaryChartLabel(label)) {
    return true;
  }

  let januaryAnchor = -1;

  for (let cursor = index; cursor >= 0; cursor -= 1) {
    if (isJanuaryChartLabel(labels[cursor])) {
      januaryAnchor = cursor;
      break;
    }
  }

  if (januaryAnchor === -1) {
    return index % 2 === 0;
  }

  return (index - januaryAnchor) % 2 === 0;
}

function StackSegment({
  height,
  color,
  rounded,
  shouldAnimate,
}: {
  height: number;
  color: string;
  rounded: boolean;
  shouldAnimate: boolean;
}) {
  const progress = useSharedValue(0);

  useEffect(() => {
    if (!shouldAnimate) {
      progress.value = 0;
      return;
    }

    progress.value = 0;
    progress.value = withTiming(1, {
      duration: 700,
      easing: Easing.out(Easing.cubic),
    });
  }, [height, progress, shouldAnimate]);

  const animatedStyle = useAnimatedStyle(() => ({
    height: height * progress.value,
  }));

  return (
    <Animated.View
      style={[
        animatedStyle,
        {
          backgroundColor: color,
          borderTopLeftRadius: rounded ? 4 : 0,
          borderTopRightRadius: rounded ? 4 : 0,
        },
      ]}
    />
  );
}

export function StackedBarChart({
  bars,
  series,
  formatValue,
  formatAxis,
  totalSuffix,
  totalSeriesKeys,
}: StackedBarChartProps) {
  const { styles } = useStyles(stylesheet);
  const { ref, isInView, onLayout } = useAnimateWhenInView();
  const sparseXLabels = ['xs', 'sm'].includes(UnistylesRuntime.breakpoint);
  const xLabels = bars.map((bar) => bar.label);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const hoveredIndex = useRef<number | null>(null);
  const [plotWidth, setPlotWidth] = useState(0);

  useEffect(() => {
    if (!sparseXLabels || bars.length === 0) {
      return;
    }

    setSelectedIndex((current) => {
      if (current !== null && current < bars.length) {
        return current;
      }

      return bars.length - 1;
    });
  }, [bars, sparseXLabels]);

  const peak = Math.max(
    0,
    ...bars.map((bar) =>
      series.reduce((sum, item) => sum + (bar.values[item.key] ?? 0), 0)
    )
  );
  const max = sparseXLabels ? mobileAxisMax(peak) : axisMax(peak);
  const shouldAnimateBars = sparseXLabels ? isInView : true;
  const ticks = [max, max / 2, 0];
  const selected = selectedIndex === null ? null : bars[selectedIndex];
  const tooltipLeft =
    selectedIndex === null || plotWidth === 0 || bars.length === 0
      ? 0
      : Math.min(
          Math.max(
            ((selectedIndex + 0.5) / bars.length) * plotWidth -
              TOOLTIP_WIDTH / 2,
            0
          ),
          Math.max(plotWidth - TOOLTIP_WIDTH, 0)
        );

  return (
    <View
      ref={sparseXLabels ? ref : undefined}
      onLayout={sparseXLabels ? onLayout : undefined}
      style={styles.chart}
    >
      <View style={styles.plotRow}>
        {sparseXLabels ? null : (
          <View style={styles.yAxis}>
            {ticks.map((tick) => (
              <Text key={tick} type="caption2" style={styles.yAxisLabel}>
                {formatAxis(tick)}
              </Text>
            ))}
          </View>
        )}

        <View
          style={styles.plot}
          onLayout={(event) => {
            setPlotWidth(event.nativeEvent.layout.width);
          }}
        >
          {ticks.map((tick) => (
            <View
              key={tick}
              style={[styles.gridline, { bottom: (tick / max) * PLOT_HEIGHT }]}
            />
          ))}

          <View style={styles.bars}>
            {bars.map((bar, index) => {
              const topKey = [...series]
                .reverse()
                .find((item) => (bar.values[item.key] ?? 0) > 0)?.key;

              return (
                <Pressable
                  key={bar.label}
                  style={styles.column}
                  onHoverIn={() => {
                    hoveredIndex.current = index;
                    setSelectedIndex(index);
                  }}
                  onHoverOut={() => {
                    requestAnimationFrame(() => {
                      if (hoveredIndex.current === index) {
                        hoveredIndex.current = null;
                        setSelectedIndex(null);
                      }
                    });
                  }}
                  onPress={() => {
                    if (sparseXLabels) {
                      setSelectedIndex(index);
                      return;
                    }

                    setSelectedIndex((current) =>
                      current === index ? null : index
                    );
                  }}
                >
                  <View style={styles.bar}>
                    {[...series].reverse().map((item) => {
                      const value = bar.values[item.key] ?? 0;
                      if (value <= 0) {
                        return null;
                      }

                      return (
                        <StackSegment
                          key={item.key}
                          height={(value / max) * PLOT_HEIGHT}
                          color={item.color}
                          rounded={item.key === topKey}
                          shouldAnimate={shouldAnimateBars}
                        />
                      );
                    })}
                  </View>
                </Pressable>
              );
            })}
          </View>

          {selected && !sparseXLabels ? (
            <View
              pointerEvents="none"
              style={[styles.tooltip, { left: tooltipLeft }]}
            >
              <Card style={styles.tooltipCard}>
                <MonthEarningsDetail
                  bar={selected}
                  series={series}
                  totalSeriesKeys={totalSeriesKeys}
                  formatValue={formatValue}
                  totalSuffix={totalSuffix}
                  styles={styles}
                />
              </Card>
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.xAxis}>
        {sparseXLabels ? null : <View style={styles.yAxisSpacer} />}
        <View style={styles.xLabels}>
          {bars.map((bar, index) => {
            const showLabel = shouldShowSparseMonthLabel(
              index,
              xLabels,
              sparseXLabels
            );
            const januaryParts =
              showLabel && sparseXLabels ? januaryLabelParts(bar.label) : null;
            const isSelected = selectedIndex === index;
            const xLabelStyle = [
              styles.xLabelText,
              isSelected && sparseXLabels ? styles.xLabelSelected : undefined,
            ];

            return (
              <View key={`${bar.label}-${index}`} style={styles.xLabelCell}>
                {showLabel && januaryParts?.year ? (
                  <View style={styles.januaryXLabel}>
                    <Text type="caption2" style={xLabelStyle}>
                      {januaryParts.month}
                    </Text>
                    <Text type="caption2" style={xLabelStyle}>
                      {januaryParts.year}
                    </Text>
                  </View>
                ) : showLabel ? (
                  <Text type="caption2" style={xLabelStyle} numberOfLines={1}>
                    {bar.label}
                  </Text>
                ) : null}
              </View>
            );
          })}
        </View>
      </View>

      {sparseXLabels && selected ? (
        <View style={styles.mobileDetailCard}>
          <MonthEarningsDetail
            bar={selected}
            series={series}
            totalSeriesKeys={totalSeriesKeys}
            formatValue={formatValue}
            totalSuffix={totalSuffix}
            variant="mobile"
            styles={styles}
          />
        </View>
      ) : null}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  chart: {
    gap: 8,
  },
  plotRow: {
    flexDirection: 'row',
    gap: 8,
  },
  yAxis: {
    width: 44,
    height: PLOT_HEIGHT,
    justifyContent: 'space-between',
  },
  yAxisLabel: {
    color: theme.colors.text.secondary,
    textAlign: 'right',
    width: '100%',
  },
  yAxisSpacer: {
    width: 52,
  },
  axisLabel: {
    color: theme.colors.text.secondary,
  },
  plot: {
    flex: 1,
    height: PLOT_HEIGHT,
  },
  gridline: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  bars: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 50,
  },
  column: {
    flex: 1,
    height: '100%',
    justifyContent: 'flex-end',
  },
  bar: {
    width: '100%',
    height: '100%',
    justifyContent: 'flex-end',
  },
  xAxis: {
    flexDirection: 'row',
  },
  xLabels: {
    flex: 1,
    flexDirection: 'row',
    gap: 50,
  },
  xLabelCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  xLabelText: {
    textAlign: 'center',
    color: theme.colors.text.secondary,
  },
  xLabelSelected: {
    color: theme.colors.text.primary,
  },
  januaryXLabel: {
    alignItems: 'center',
    gap: 0,
  },
  mobileDetailCard: {
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.card.hover.borderColor,
    backgroundColor: theme.colors.card.hover.backgroundColor,
  },
  mobileDetailContent: {
    gap: 8,
  },
  mobileDetailHeader: {
    gap: 2,
  },
  mobileDetailTotal: {
    color: theme.colors.text.primary,
  },
  mobileDetailRows: {
    gap: 4,
  },
  mobileDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mobileDetailValue: {
    flexShrink: 0,
    minWidth: 72,
    textAlign: 'right',
    color: theme.colors.text.primary,
  },
  tooltip: {
    position: 'absolute',
    top: -50,
    width: TOOLTIP_WIDTH,
    zIndex: 2,
  },
  tooltipCard: {
    padding: 12,
    gap: 6,
  },
  tooltipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tooltipLabel: {
    flex: 1,
    color: theme.colors.text.secondary,
  },
  tooltipValue: {
    flexShrink: 0,
    textAlign: 'right',
  },
  tooltipCaption: {
    color: theme.colors.text.secondary,
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  swatchRound: {
    borderRadius: 100,
  },
}));
